<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="600px"
        transition="slide-y-transition"
        eager
        @keydown.esc="handleClose"
        @click:outside="handleClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Edytuj pozycję nr <strong>{{ rubberSerialNumber }}</strong>
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <v-form @submit.prevent>
                    <v-autocomplete
                        v-model="selectedJobId"
                        :items="additionalJobOptions"
                        :loading="searchLoading"
                        :search-input.sync="searchTerm"
                        :no-data-text="noDataText"
                        :disabled="saveLoading"
                        clearable
                        hide-selected
                        hide-details="auto"
                        item-text="primaryNumber"
                        item-value="id"
                        label="Numer zlecenia"
                        solo
                        @input="handleJobChange"
                    />

                    <div v-if="selectedJob" class="mt-6 mb-1 font-weight-medium text-body-1">
                        Wybierz numer montażu:
                    </div>

                    <v-row v-if="impositionOptions.length" dense>
                        <v-col v-for="(plate, index) in impositionOptions" :key="plate.id" cols="12" sm="4">
                            <v-checkbox
                                v-model="selectedImpositions"
                                :value="plate.plateNumber"
                                :label="`Montaż ${plate.plateNumber}`"
                                :disabled="saveLoading"
                                :error-messages="index === 0 ? impositionErrorMessage : ''"
                                :error="showImpositionError"
                                color="primary"
                                :hide-details="index === 0 ? 'auto' : true"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions>
                <v-btn color="error" text :disabled="saveLoading" @click="handleClose">
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn color="success" text :loading="saveLoading" @click="handleSubmit">
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'EditAdditionalJobNumbersDialog',
    props: {
        isOpen: {
            type: Boolean
        },
        rubber: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            selectedJobId: null,
            selectedImpositions: [],
            searchTerm: '',
            validationAttempted: false,
            searchTimeout: null,
            isSelecting: false,
            hasSearched: false
        }
    },
    computed: {
        rubberSerialNumber() {
            return this.rubber && this.rubber.serialNumber ? this.rubber.serialNumber : '-'
        },
        additionalJobOptions() {
            return this.$store.state.configurations.rubberRegistry.additionalJobSearchResults || []
        },
        searchLoading() {
            return this.$store.state.configurations.rubberRegistry.additionalJobSearchLoading
        },
        saveLoading() {
            return this.$store.state.configurations.rubberRegistry.additionalJobSaveLoading
        },
        selectedJob() {
            return this.additionalJobOptions.find(job => job.id === this.selectedJobId) || null
        },
        impositionOptions() {
            if (!this.selectedJob || !Array.isArray(this.selectedJob.reportItemPlates)) {
                return []
            }

            return this.selectedJob.reportItemPlates
        },
        noDataText() {
            const trimmedSearch = (this.searchTerm || '').trim()

            if (trimmedSearch.length < 3) {
                return 'Wpisz co najmniej 3 znaki, aby wyszukać.'
            }

            if (this.hasSearched && !this.searchLoading && this.additionalJobOptions.length === 0) {
                return 'Nie znaleziono zleceń spełniających kryteria.'
            }

            return 'Wpisz co najmniej 3 znaki, aby wyszukać.'
        },
        showImpositionError() {
            return this.validationAttempted && this.impositionOptions.length > 0 && !this.selectedImpositions.length
        },
        impositionErrorMessage() {
            return this.showImpositionError ? 'Wybierz co najmniej jeden montaż.' : ''
        },
        isFormValid() {
            return Boolean(this.selectedJob && this.selectedImpositions.length && this.rubber && this.rubber.id)
        }
    },
    watch: {
        isOpen(newValue) {
            if (newValue) {
                this.resetForm()
            } else {
                this.onDialogClosed()
            }
        },
        selectedJobId(newValue, oldValue) {
            if (newValue === oldValue) {
                return
            }

            this.isSelecting = true
            this.validationAttempted = false

            if (!newValue) {
                this.selectedImpositions = []
                setTimeout(() => {
                    this.isSelecting = false
                }, 100)

                return
            }

            const job = this.additionalJobOptions.find(j => j.id === newValue)
            const plates = job?.reportItemPlates || []

            if (plates.length === 1) {
                this.selectedImpositions = [plates[0].plateNumber]
            } else {
                this.selectedImpositions = []
            }

            setTimeout(() => {
                this.isSelecting = false
            }, 100)
        },
        searchTerm(newValue, oldValue) {
            if (this.isSelecting) {
                return
            }

            // If the new value matches an existing option's primaryNumber, don't search
            const matchesExistingOption = this.additionalJobOptions.some(
                option => option.primaryNumber === newValue
            )

            if (matchesExistingOption && this.selectedJobId) {
                return
            }

            if (newValue !== oldValue) {
                this.enqueueSearch(newValue)
            }
        }
    },
    beforeDestroy() {
        this.clearSearchTimeout()
    },
    methods: {
        handleJobChange() {
            this.validationAttempted = false
        },
        enqueueSearch(value) {
            this.clearSearchTimeout()

            this.searchTimeout = setTimeout(() => {
                this.performSearch(value)
            }, 500)
        },
        performSearch(value) {
            const trimmedValue = (value || '').trim()

            if (trimmedValue.length >= 3) {
                this.hasSearched = true
            } else {
                this.hasSearched = false
            }

            this.$store.dispatch('configurations/searchAdditionalJobNumbers', {
                search: value,
                limit: 20
            })
        },
        clearSearchTimeout() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
        },
        resetForm() {
            this.selectedJobId = null
            this.selectedImpositions = []
            this.searchTerm = ''
            this.validationAttempted = false
            this.isSelecting = false
            this.hasSearched = false
            this.clearSearchTimeout()
        },
        onDialogClosed() {
            this.resetForm()
            this.$store.dispatch('configurations/clearAdditionalJobNumbers')
        },
        handleSubmit() {
            this.validationAttempted = true

            if (!this.isFormValid) {
                return
            }

            const payload = {
                rubberId: this.rubber.id,
                additionalJobNumbers: this.selectedImpositions.map(impositionNumber => ({
                    name: this.selectedJob.primaryNumber,
                    impositionNumber
                }))
            }

            this.$emit('on-save', payload)
        },
        handleClose() {
            if (this.saveLoading) {
                return
            }

            this.$emit('on-close')
        },
    }
}
</script>
